/**
 * Minimal CSV helpers for settings templates (Jurisdiction, Methodology, Scoring).
 *
 * Profiles are flat key/value configs, so we use a two-column `field,value`
 * CSV with dotted-path keys to represent nested structures (e.g.
 * `districtWeights.city`, `profiles.new_startup.thresholds.fiscalReturn.roiExcellent`).
 *
 * Human-editable in Excel/Sheets, trivial to parse, and the dotted-path
 * approach keeps round-trips lossless even for the scoring rubric's
 * three-profile nesting.
 */

export type CsvRow = readonly [string, string];

/** Quote a CSV cell if it contains `,`, `"`, CR, or LF. */
function quote(v: string): string {
  if (/[",\r\n]/.test(v)) return `"${v.replace(/"/g, '""')}"`;
  return v;
}

/** Serialize rows to a CSV string with a `field,value` header. */
export function rowsToCsv(rows: readonly CsvRow[]): string {
  const lines = ['field,value'];
  for (const [k, v] of rows) lines.push(`${quote(k)},${quote(v)}`);
  return `${lines.join('\n')}\n`;
}

/**
 * Parse a CSV with a `field,value` header (header row is optional — if the
 * first line literally is `field,value` we skip it).
 *
 * Returns a map of trimmed keys → string values. Empty values are preserved
 * (callers decide what blank means — e.g. "inherit" for override fields).
 */
export function parseCsv(text: string): Record<string, string> {
  const out: Record<string, string> = {};
  const rows = parseCsvRows(text);
  if (rows.length === 0) return out;
  let start = 0;
  const first = rows[0];
  if (
    first &&
    first.length >= 2 &&
    (first[0] ?? '').toLowerCase() === 'field' &&
    (first[1] ?? '').toLowerCase() === 'value'
  ) {
    start = 1;
  }
  for (let i = start; i < rows.length; i++) {
    const r = rows[i];
    if (!r || r.length === 0) continue;
    const key = (r[0] ?? '').trim();
    if (!key) continue;
    out[key] = (r[1] ?? '').trim();
  }
  return out;
}

/** Low-level CSV row parser supporting quoted cells. */
function parseCsvRows(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') {
          cell += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        cell += c;
      }
    } else if (c === '"') {
      inQuotes = true;
    } else if (c === ',') {
      row.push(cell);
      cell = '';
    } else if (c === '\r') {
      // ignore — \n handles the row break
    } else if (c === '\n') {
      row.push(cell);
      rows.push(row);
      row = [];
      cell = '';
    } else {
      cell += c;
    }
  }
  if (cell.length > 0 || row.length > 0) {
    row.push(cell);
    rows.push(row);
  }
  // Trim trailing empty rows
  while (rows.length > 0 && (rows[rows.length - 1] ?? []).every((c) => c === '')) rows.pop();
  return rows;
}

/** Trigger a client-side download of a CSV string. */
export function downloadCsvFile(filename: string, csv: string): void {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Prompt the user to pick a file and return its text contents. */
export async function pickCsvFile(): Promise<string | null> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.csv,text/csv';
    input.onchange = () => {
      const file = input.files?.[0];
      if (!file) return resolve(null);
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result ?? ''));
      reader.onerror = () => resolve(null);
      reader.readAsText(file);
    };
    input.click();
  });
}

/**
 * Flatten a nested object into dotted-path string rows.
 * Arrays serialize as pipe-joined values (e.g. `capitalInvestment|netFiscalImpact`).
 */
export function flattenToRows(obj: unknown, prefix = '', out: CsvRow[] = []): CsvRow[] {
  if (obj === null || obj === undefined) {
    out.push([prefix, '']);
    return out;
  }
  if (Array.isArray(obj)) {
    out.push([prefix, obj.map((v) => String(v)).join('|')]);
    return out;
  }
  if (typeof obj === 'object') {
    for (const [k, v] of Object.entries(obj as Record<string, unknown>)) {
      const path = prefix ? `${prefix}.${k}` : k;
      flattenToRows(v, path, out);
    }
    return out;
  }
  if (typeof obj === 'boolean') {
    out.push([prefix, obj ? 'true' : 'false']);
    return out;
  }
  out.push([prefix, String(obj)]);
  return out;
}

/**
 * Expand a flat `{ "a.b.c": "1" }` map back into a nested object.
 * Pipe-joined values are split into arrays. String numerics/bools are
 * coerced only when the caller opts in via `coerce`.
 */
export function unflattenRows(
  flat: Record<string, string>,
  opts: { coerce?: boolean } = {},
): Record<string, unknown> {
  const root: Record<string, unknown> = {};
  for (const [path, raw] of Object.entries(flat)) {
    const keys = path.split('.').filter((k) => k.length > 0);
    if (keys.length === 0) continue;
    let node = root;
    for (let i = 0; i < keys.length - 1; i++) {
      const k = keys[i] as string;
      const cur = node[k];
      if (typeof cur !== 'object' || cur === null || Array.isArray(cur)) {
        node[k] = {};
      }
      node = node[k] as Record<string, unknown>;
    }
    const leaf = keys[keys.length - 1] as string;
    node[leaf] = opts.coerce ? coerceValue(raw) : raw;
  }
  return root;
}

function coerceValue(raw: string): unknown {
  if (raw === '') return '';
  if (raw === 'true') return true;
  if (raw === 'false') return false;
  if (raw.includes('|'))
    return raw
      .split('|')
      .map((s) => s.trim())
      .filter(Boolean);
  const n = Number(raw);
  if (Number.isFinite(n) && raw.trim() !== '') return n;
  return raw;
}
